
/* Just Add Baking — premium gold confetti effect
   Lightweight, dependency-free, and scoped to .jab-confetti-card only.

   Motion system rebuild: vertical fall, drift and rotation are driven
   by a single 2-point (0%/100%) keyframe animation with a LINEAR
   timing function — mathematically this means the browser
   interpolates every frame at a constant rate with no eased stops in
   between, so there is nothing for the eye to read as a waypoint,
   jump, or redirect. Horizontal sway is intentionally NOT part of
   that same animation — it lives on a separate, nested inner element
   with its OWN 2-point animation using ease-in-out + alternate +
   infinite, which is the standard CSS technique for a smooth,
   continuous pendulum/sine-like swing (ease-in-out on an alternating
   2-point animation naturally decelerates into each turnaround rather
   than reversing sharply). The two transforms compose because they're
   on different, nested DOM elements, not fighting over the same
   `transform` property on one element.
*/
(() => {
  const CARD_SELECTOR = '.jab-confetti-card';
  const PIECES_DESKTOP = 60;
  const PIECES_MOBILE = 36;
  const GOLD_VARIANTS = ['jab-gold-a', 'jab-gold-b', 'jab-gold-c'];

  const rand = (min, max) => Math.random() * (max - min) + min;
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

  function pickDepth() {
    const r = Math.random();
    if (r < 0.12) return 'fg';
    if (r < 0.32) return 'bg';
    return 'mid';
  }

  function pickShape() {
    const r = Math.random();
    if (r < 0.4) return 'is-fleck';
    if (r < 0.78) return 'is-strip';
    return 'is-ribbon';
  }

  function buildEffect(card) {
    if (card.dataset.jabConfettiReady === 'true') return;
    card.dataset.jabConfettiReady = 'true';

    const layer = document.createElement('div');
    layer.className = 'jab-confetti-layer';
    layer.setAttribute('aria-hidden', 'true');

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const count = isMobile ? PIECES_MOBILE : PIECES_DESKTOP;

    for (let i = 0; i < count; i++) {
      const shape = pickShape();
      const depth = pickDepth();
      // About 1 in 4 midground/foreground pieces spiral/twist in 3D
      // rather than rotating flat — background pieces stay flat since
      // the blur already reads as depth without needing more motion.
      const spiral = depth !== 'bg' && Math.random() < 0.28;

      // ---- outer wrapper: vertical fall + drift + rotation only ----
      const piece = document.createElement('span');
      piece.className = `jab-confetti-piece is-depth-${depth}${spiral ? ' is-spiral' : ''}`;

      const x = rand(6, 94);
      const drift = rand(-30, 30); // gentle overall wind push, kept subtle per request
      const rot = rand(180, 420) * (Math.random() > .5 ? 1 : -1);
      const spin = rand(280, 620) * (Math.random() > .5 ? 1 : -1);

      // Cinematic entrance: a handful appear almost immediately
      // (0–0.5s), the rest stagger in through the "main confetti
      // falls" window (0.5–2.5s).
      const delay = i < Math.round(count * 0.15)
        ? rand(0, .5)
        : rand(.4, 2.3);

      let sizeMul = 1, opacityBase, durationRange, blur = 0;
      if (depth === 'fg') {
        sizeMul = rand(1.25, 1.55);
        opacityBase = rand(.78, .97);
        durationRange = [4.8, 6.2];
      } else if (depth === 'bg') {
        sizeMul = rand(.5, .72);
        opacityBase = rand(.18, .38);
        durationRange = [6.4, 8.4];
        blur = rand(.4, .9);
      } else {
        sizeMul = rand(.85, 1.1);
        opacityBase = rand(.45, .82);
        durationRange = [5.4, 7.2];
      }

      const duration = rand(durationRange[0], durationRange[1]);
      const fall = rand(650, 900) * (depth === 'fg' ? 1.08 : depth === 'bg' ? 0.92 : 1);

      let width, height;
      if (shape === 'is-fleck') {
        width = rand(2.6, 4.2) * sizeMul;
        height = rand(3.4, 5.5) * sizeMul;
      } else if (shape === 'is-strip') {
        width = rand(1.4, 2.2) * sizeMul;
        height = rand(9, 15) * sizeMul;
      } else {
        width = rand(2, 3) * sizeMul;
        height = rand(15, 24) * sizeMul;
      }

      piece.style.setProperty('--x', `${x}%`);
      piece.style.setProperty('--drift', `${drift}px`);
      piece.style.setProperty('--fall', `${fall}px`);
      piece.style.setProperty('--rot', `${rot}deg`);
      piece.style.setProperty('--spin', `${spin}deg`);
      piece.style.setProperty('--delay', `${delay}s`);
      piece.style.setProperty('--duration', `${duration}s`);
      piece.style.setProperty('--w', `${width}px`);
      piece.style.setProperty('--h', `${height}px`);
      piece.style.setProperty('--opacity', opacityBase.toFixed(2));
      piece.style.setProperty('--blur', `${blur}px`);

      // ---- inner element: shape, gradient, and its OWN independent
      // sway animation — a slow, gentle pendulum, never synced to the
      // same timeline as the fall so it never reads as a scripted
      // waypoint tied to a specific vertical position. ----
      const inner = document.createElement('span');
      inner.className = `jab-confetti-piece__inner ${shape} ${pick(GOLD_VARIANTS)}`;

      const swayAmplitude = rand(3, 9); // very subtle, per request
      const swayDuration = rand(3.5, 6.5); // independent of fall duration
      const swayDelay = rand(0, 2);

      inner.style.setProperty('--sway', `${swayAmplitude}px`);
      inner.style.setProperty('--sway-duration', `${swayDuration}s`);
      inner.style.setProperty('--sway-delay', `${swayDelay}s`);

      piece.appendChild(inner);
      layer.appendChild(piece);
    }

    // "Arrival" particles — tiny gold points that appear near the
    // underline as the main confetti wave finishes falling (timed to
    // the 1.5–3.0s "reaching/fading near the underline" window).
    const arrivalCount = isMobile ? 6 : 9;
    for (let i = 0; i < arrivalCount; i++) {
      const spark = document.createElement('span');
      spark.className = 'jab-confetti-spark';
      spark.style.left = `${rand(14, 86)}%`;
      spark.style.setProperty('--spark-delay', `${rand(1.5, 3)}s`);
      spark.style.setProperty('--spark-scale', rand(.7, 1.4).toFixed(2));
      layer.appendChild(spark);
    }

    // "Gold dust" — a one-time burst of 6–10 extremely small specks
    // immediately around the underline, timed to when the line
    // finishes forming (3.0–3.8s). Separate from the arrival sparks
    // above, which happen earlier and loop; these fire once.
    const dustCount = Math.round(rand(6, 10));
    for (let i = 0; i < dustCount; i++) {
      const dust = document.createElement('span');
      dust.className = 'jab-gold-dust';
      dust.setAttribute('aria-hidden', 'true');
      dust.style.left = `${rand(30, 70)}%`;
      dust.style.setProperty('--dust-delay', `${rand(3, 3.6)}s`);
      dust.style.setProperty('--dust-x', `${rand(-40, 40)}px`);
      dust.style.setProperty('--dust-y', `${rand(-8, 8)}px`);
      layer.appendChild(dust);
    }

    let underline = card.querySelector('.jab-confetti-underline');
    if (!underline) {
      underline = document.createElement('div');
      underline.className = 'jab-confetti-underline';
      underline.setAttribute('aria-hidden', 'true');
      card.appendChild(underline);
    }

    card.prepend(layer);
  }

  function activate(card) {
    buildEffect(card);

    // Run once per page view to avoid looking busy.
    if (card.dataset.jabConfettiPlayed === 'true') return;
    card.dataset.jabConfettiPlayed = 'true';

    requestAnimationFrame(() => {
      card.classList.add('is-confetti-active');
    });
  }

  function init() {
    const cards = [...document.querySelectorAll(CARD_SELECTOR)];
    if (!cards.length) return;

    if (!('IntersectionObserver' in window)) {
      cards.forEach(activate);
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.12) {
          activate(entry.target);
          obs.unobserve(entry.target);
        }
      });
    }, {
      threshold: [0.12]
    });

    cards.forEach(card => observer.observe(card));
  }

  document.addEventListener('DOMContentLoaded', init);

  // Shopify Theme Editor support.
  document.addEventListener('shopify:section:load', init);
})();
